/**
 * TherapistVerification.tsx - Validación de acceso para terapeutas
 * 
 * 🧑‍⚕️ Componente de autenticación con diseño minimalista:
 * - Validación por RUT siguiendo el mismo estilo visual
 * - Integración con el sistema de diseño existente
 * - Feedback visual profesional y ergonómico
 * 
 * @version 1.0.0
 * @author Conectemos Chile
 */

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TherapistValidationService, { AuthenticationResult } from '../../services/therapist-validation.service';
import './TherapistVerification.css';

interface TherapistVerificationProps {
  onAuthenticated: (result: AuthenticationResult) => void;
  initialError?: string;
}

interface WelcomeUser {
  nombre: string;
  apellido: string;
  especialidad: string;
  avatar?: string;
  numeroLicencia?: string;
}

const TherapistVerification: React.FC<TherapistVerificationProps> = ({ onAuthenticated, initialError }) => {
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeUser, setWelcomeUser] = useState<WelcomeUser | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const isFormValid = emailInput.trim().length > 0 && passwordInput.length >= 6;
  const isAnyLoading = isValidating || isGoogleLoading;

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setError(null);
    try {
      await TherapistValidationService.authenticateWithGoogle();
    } catch {
      setError('Error al conectar con Google. Intentá nuevamente.');
      setIsGoogleLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid) {
      handleLogin();
    }
  };

  const handleLogin = async () => {
    if (!isFormValid) {
      setError('Ingresá tu email y contraseña');
      emailInputRef.current?.focus();
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await TherapistValidationService.authenticateTherapist(emailInput, passwordInput);

      if (!result.found || (!result.therapist && !result.investor)) {
        setError(result.message || 'Credenciales inválidas. Verificá tu email y contraseña.');
        setIsValidating(false);
        emailInputRef.current?.focus();
        return;
      }

      if (result.userType === 'investor' && result.investor) {
        setWelcomeUser({
          nombre: result.investor.nombre,
          apellido: result.investor.apellido,
          especialidad: `Inversionista · ${result.investor.tipo_inversionista}`,
        });
      } else if (result.therapist) {
        setWelcomeUser({
          nombre: result.therapist.nombre,
          apellido: result.therapist.apellido,
          especialidad: result.therapist.especialidad,
          avatar: result.therapist.avatar,
          numeroLicencia: result.therapist.numeroLicencia,
        });
      }

      setShowWelcome(true);

      setTimeout(() => {
        onAuthenticated(result);
      }, 2000);

    } catch (err) {
      console.error('❌ Error en login:', err);
      setError('Error de conexión. Por favor intentá nuevamente.');
      setIsValidating(false);
      emailInputRef.current?.focus();
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="therapist-verification">
      <div className="verification-container">
        {/* Header */}
        <motion.div 
          className="verification-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="logo-section">
            <span className="logo-mark">
              <img
                src="/assets/img/logo.png"
                alt="Conectemos Chile"
                className="logo-mark__img"
                width="112"
                height="112"
              />
            </span>
            <h1>Portal Profesional</h1>
            <p>Conectemos Chile</p>
          </div>
        </motion.div>

        {/* Formulario de validación */}
        <AnimatePresence mode="wait">
          {!showWelcome ? (
            <motion.div
              key="verification-form"
              className="verification-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <div className="form-section">
                <h2>Acceso Profesional</h2>
                <p className="form-subtitle">
                  Ingresá tus credenciales para acceder a tu panel de trabajo
                </p>

                <div className="input-group">
                  <label htmlFor="therapist-email">Email</label>
                  <div className="input-container">
                    <input
                      ref={emailInputRef}
                      id="therapist-email"
                      type="email"
                      value={emailInput}
                      onChange={e => { setEmailInput(e.target.value); setError(null); }}
                      onKeyDown={handleKeyDown}
                      placeholder="tu@email.cl"
                      disabled={isValidating}
                      autoComplete="email"
                      autoFocus
                    />
                    <div className="input-icon">
                      <i className="fas fa-envelope"></i>
                    </div>
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="therapist-password">Contraseña</label>
                  <div className="input-container">
                    <div className="input-icon">
                      <i className="fas fa-lock"></i>
                    </div>
                    <input
                      id="therapist-password"
                      type={showPassword ? 'text' : 'password'}
                      value={passwordInput}
                      onChange={e => { setPasswordInput(e.target.value); setError(null); }}
                      onKeyDown={handleKeyDown}
                      placeholder="••••••••"
                      disabled={isValidating}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="input-icon input-icon--btn"
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                  </motion.div>
                )}

                <button
                  className={`validate-btn ${isValidating ? 'loading' : ''} ${isFormValid ? 'enabled' : ''}`}
                  onClick={handleLogin}
                  disabled={!isFormValid || isAnyLoading}
                >
                  {isValidating ? (
                    <>
                      <div className="spinner"></div>
                      Ingresando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt"></i>
                      Ingresar al Portal
                    </>
                  )}
                </button>

                <div className="oauth-divider">
                  <span>o ingresá con</span>
                </div>

                <button
                  className={`google-btn ${isGoogleLoading ? 'loading' : ''}`}
                  onClick={handleGoogleLogin}
                  disabled={isAnyLoading}
                  type="button"
                >
                  {isGoogleLoading ? (
                    <>
                      <div className="spinner spinner--dark"></div>
                      Conectando con Google...
                    </>
                  ) : (
                    <>
                      <svg className="google-icon" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continuar con Google
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="welcome-screen"
              className="welcome-screen"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <div className="welcome-content">
                <div className="success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h2>¡Bienvenido/a!</h2>
                <div className="therapist-info">
                  <div className="therapist-avatar">
                    {welcomeUser?.avatar ? (
                      <img src={welcomeUser.avatar} alt="Avatar" />
                    ) : (
                      <div className="avatar-placeholder">
                        <i className="fas fa-user-md"></i>
                      </div>
                    )}
                  </div>
                  <div className="therapist-details">
                    <h3>
                      {welcomeUser?.nombre} {welcomeUser?.apellido}
                    </h3>
                    <p className="speciality">{welcomeUser?.especialidad}</p>
                    {welcomeUser?.numeroLicencia && (
                      <p className="license">Lic. {welcomeUser.numeroLicencia}</p>
                    )}
                  </div>
                </div>
                <p className="loading-message">
                  Preparando tu panel de trabajo...
                </p>
                <div className="loading-progress">
                  <div className="progress-bar"></div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <motion.div 
          className="verification-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <p>
            ¿Problemas de acceso? 
            <a href="mailto:soporte@conectemos.cl"> Contacta soporte</a>
          </p>
          <div className="security-info">
            <i className="fas fa-shield-alt"></i>
            <span>Conexión segura SSL</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TherapistVerification;